import {
  type ExpenseCategory,
  type InvestmentProperty,
  type Portfolio,
  createDefaultExpenseCategories
} from "@aus-property/core";
import { createId } from "@/utils/id";

const nowIso = () => new Date().toISOString();

const cloneCategories = (categories: ExpenseCategory[]): ExpenseCategory[] =>
  categories.map((category) => ({
    ...category,
    items: category.items.map((item) => ({ ...item }))
  }));

const defaultProperty = (
  input: Partial<InvestmentProperty> = {}
): InvestmentProperty => ({
  id: input.id ?? createId("property"),
  name: input.name ?? "New Property",
  address: input.address ?? "",
  state: input.state ?? "NSW",
  kind: input.kind ?? "HOUSE",
  notes: input.notes,
  purchase: {
    purchaseDate: input.purchase?.purchaseDate ?? "2024-07-01",
    price: input.purchase?.price ?? 850000,
    deposit: input.purchase?.deposit ?? 170000,
    stampDuty: input.purchase?.stampDuty ?? 35000,
    legalFees: input.purchase?.legalFees ?? 1900,
    inspections: input.purchase?.inspections ?? 650,
    lmi: input.purchase?.lmi ?? 0,
    renovationCapex: input.purchase?.renovationCapex ?? 5000
  },
  loan: {
    principal: input.loan?.principal ?? 680000,
    interestRate: input.loan?.interestRate ?? 6.1,
    type: input.loan?.type ?? "P_AND_I",
    termYears: input.loan?.termYears ?? 30,
    ioYears: input.loan?.ioYears ?? 0,
    offsetBalance: input.loan?.offsetBalance ?? 15000,
    annualFees: input.loan?.annualFees ?? 480
  },
  income: {
    weeklyRent: input.income?.weeklyRent ?? 760,
    otherAnnualIncome: input.income?.otherAnnualIncome ?? 0,
    rentGrowthRate: input.income?.rentGrowthRate ?? 3
  },
  expenses: {
    managementPercent: input.expenses?.managementPercent ?? 6.5,
    // Prefer itemised Agent Fees category (letting/renewals/inspections) for clarity.
    agentFeesAnnual: input.expenses?.agentFeesAnnual ?? 0,
    councilRates: input.expenses?.councilRates ?? 2100,
    insurance: input.expenses?.insurance ?? 1500,
    maintenance: input.expenses?.maintenance ?? 2200,
    strata: input.expenses?.strata ?? 0,
    water: input.expenses?.water ?? 1000,
    utilities: input.expenses?.utilities ?? 0,
    vacancyRate: input.expenses?.vacancyRate ?? 2,
    expenseInflationRate: input.expenses?.expenseInflationRate ?? 2.5,
    categories:
      input.expenses?.categories && input.expenses.categories.length > 0
        ? cloneCategories(input.expenses.categories)
        : cloneCategories(createDefaultExpenseCategories())
  },
  tax: {
    marginalTaxRate: input.tax?.marginalTaxRate ?? 37,
    annualDepreciation: input.tax?.annualDepreciation ?? 5500,
    negativeGearingEnabled: input.tax?.negativeGearingEnabled ?? true
  },
  growth: {
    capitalGrowthRate: input.growth?.capitalGrowthRate ?? 5,
    inflationRate: input.growth?.inflationRate ?? 2.5,
    expenseInflationRate: input.growth?.expenseInflationRate ?? 2.5,
    rentGrowthRate: input.growth?.rentGrowthRate ?? 3
  },
  attachments: input.attachments ?? [],
  createdAt: input.createdAt ?? nowIso(),
  updatedAt: nowIso()
});

export const createPropertyTemplate = (): InvestmentProperty =>
  defaultProperty();

export const demoPortfolio = (): Portfolio => ({
  id: "portfolio-demo",
  name: "Demo Portfolio",
  assumptions: {
    forecastYears: 20,
    defaultCapitalGrowthRate: 5,
    defaultRentGrowthRate: 3,
    defaultExpenseInflationRate: 2.5,
    defaultInflationRate: 2.5
  },
  properties: [
    defaultProperty({
      id: "prop-demo-1",
      name: "Paddington Terrace",
      address: "31 Terrace St, Paddington NSW",
      state: "NSW",
      kind: "TOWNHOUSE",
      purchase: {
        purchaseDate: "2021-02-01",
        price: 1450000,
        deposit: 290000,
        stampDuty: 62000,
        legalFees: 2200,
        inspections: 900,
        lmi: 0,
        renovationCapex: 14000
      },
      loan: {
        principal: 1160000,
        interestRate: 6.2,
        type: "P_AND_I",
        termYears: 30,
        ioYears: 0,
        offsetBalance: 95000,
        annualFees: 620
      },
      income: {
        weeklyRent: 1450,
        otherAnnualIncome: 0,
        rentGrowthRate: 3.2
      },
      expenses: {
        managementPercent: 5.5,
        agentFeesAnnual: 0,
        councilRates: 2650,
        insurance: 1950,
        maintenance: 3400,
        strata: 0,
        water: 1200,
        utilities: 0,
        vacancyRate: 1.2,
        expenseInflationRate: 2.7,
        categories: [
          {
            id: "cat-agent-1",
            name: "Agent Fees",
            items: [
              {
                id: "agent-letting-1",
                name: "Letting fee",
                amount: 1450,
                cadence: "ANNUAL",
                growthRate: 2.8
              },
              {
                id: "agent-inspection-1",
                name: "Routine inspection fee",
                amount: 65,
                cadence: "MONTHLY",
                growthRate: 2.8
              }
            ]
          },
          {
            id: "cat-repairs-1",
            name: "Repairs",
            items: [
              {
                id: "repair-routine-1",
                name: "Routine repairs",
                amount: 180,
                cadence: "MONTHLY",
                growthRate: 3
              },
              {
                id: "repair-major-1",
                name: "Roof repaint",
                amount: 12500,
                cadence: "ONE_OFF",
                appliesInYears: [5, 12],
                growthRate: 3
              }
            ]
          }
        ]
      }
    }),
    defaultProperty({
      id: "prop-demo-2",
      name: "Southbank Apartment",
      address: "58 Riverside Quay, Southbank VIC",
      state: "VIC",
      kind: "UNIT",
      purchase: {
        purchaseDate: "2022-09-12",
        price: 810000,
        deposit: 162000,
        stampDuty: 44000,
        legalFees: 1800,
        inspections: 650,
        lmi: 0,
        renovationCapex: 6000
      },
      loan: {
        principal: 648000,
        interestRate: 6,
        type: "IO",
        termYears: 30,
        ioYears: 5,
        offsetBalance: 22000,
        annualFees: 540
      },
      income: {
        weeklyRent: 920,
        otherAnnualIncome: 1200,
        rentGrowthRate: 2.8
      },
      expenses: {
        managementPercent: 6,
        agentFeesAnnual: 0,
        councilRates: 1900,
        insurance: 1400,
        maintenance: 1800,
        strata: 4200,
        water: 850,
        utilities: 0,
        vacancyRate: 2.5,
        expenseInflationRate: 2.4,
        categories: [
          {
            id: "cat-agent-2",
            name: "Agent Fees",
            items: [
              {
                id: "agent-letting-2",
                name: "Letting fee",
                amount: 900,
                cadence: "ANNUAL",
                growthRate: 2.5
              }
            ]
          },
          {
            id: "cat-repairs-2",
            name: "Repairs",
            items: [
              {
                id: "repair-routine-2",
                name: "Routine repairs",
                amount: 90,
                cadence: "MONTHLY",
                growthRate: 2.4
              },
              {
                id: "repair-major-2",
                name: "Lift levy",
                amount: 5200,
                cadence: "ONE_OFF",
                appliesInYears: [3, 9],
                growthRate: 2.4
              }
            ]
          }
        ]
      }
    })
  ],
  createdAt: nowIso(),
  updatedAt: nowIso()
});
